import { FileUp, Send, X } from 'lucide-react'
import { useEffect, useRef, useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/cn'

interface MessageComposerProps {
  enabled: boolean
  disabledReason: string
  sending: boolean
  onSend(input: { text: string; file?: File }): Promise<void>
}

export function MessageComposer({
  enabled,
  disabledReason,
  sending,
  onSend,
}: MessageComposerProps) {
  const [text, setText] = useState('')
  const [file, setFile] = useState<File>()
  const textarea = useRef<HTMLTextAreaElement>(null)
  const fileInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!textarea.current) return
    textarea.current.style.height = 'auto'
    textarea.current.style.height = `${Math.min(textarea.current.scrollHeight, 144)}px`
  }, [text])

  const submit = async () => {
    if (!enabled || sending || (!text.trim() && !file)) return
    await onSend({ text: text.trim(), ...(file ? { file } : {}) })
    setText('')
    setFile(undefined)
    if (fileInput.current) fileInput.current.value = ''
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault()
      void submit()
    }
  }

  const selectFile = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0]
    if (selected) setFile(selected)
  }

  return (
    <div className="border-t border-[var(--border)] bg-[var(--surface)] px-3 pb-[calc(12px+env(safe-area-inset-bottom))] pt-3 sm:px-5 sm:pb-4">
      {!enabled && (
        <div className="mb-3 rounded-xl border border-[var(--warning-border)] bg-[var(--warning-soft)] px-3 py-2.5 text-sm leading-5 text-[var(--warning)]">
          {disabledReason}. Per continuare sarà necessario un template approvato.
        </div>
      )}
      {file && (
        <div className="mb-2 flex items-center gap-2 rounded-xl bg-[var(--muted)] px-3 py-2 text-sm">
          <FileUp className="size-4 shrink-0 text-[var(--primary)]" />
          <span className="min-w-0 flex-1 truncate font-medium">{file.name}</span>
          <button
            type="button"
            className="grid size-8 place-items-center rounded-lg hover:bg-[var(--border)]"
            aria-label="Rimuovi allegato"
            onClick={() => setFile(undefined)}
          >
            <X className="size-4" />
          </button>
        </div>
      )}
      <div
        className={cn(
          'flex items-end gap-2 rounded-2xl border border-[var(--border)] bg-[var(--surface-elevated)] p-1.5 shadow-sm transition',
          'focus-within:border-[var(--primary)] focus-within:ring-3 focus-within:ring-[var(--primary-soft)]',
          !enabled && 'opacity-65',
        )}
      >
        <input
          ref={fileInput}
          type="file"
          className="sr-only"
          accept="image/jpeg,image/png,image/webp,application/pdf,text/plain,.docx"
          onChange={selectFile}
          disabled={!enabled || sending}
        />
        <Button
          variant="ghost"
          size="icon"
          className="size-10 shrink-0"
          aria-label="Allega file"
          onClick={() => fileInput.current?.click()}
          disabled={!enabled || sending}
        >
          <FileUp className="size-5" />
        </Button>
        <textarea
          ref={textarea}
          rows={1}
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={onKeyDown}
          maxLength={4096}
          disabled={!enabled || sending}
          placeholder={enabled ? 'Scrivi un messaggio…' : 'Finestra di risposta chiusa'}
          aria-label="Messaggio"
          className="max-h-36 min-h-10 min-w-0 flex-1 resize-none bg-transparent px-1 py-2 text-base leading-6 outline-none placeholder:text-[var(--muted-foreground)] disabled:cursor-not-allowed"
        />
        <Button
          size="icon"
          className="size-10 shrink-0 rounded-xl"
          aria-label="Invia messaggio"
          disabled={!enabled || sending || (!text.trim() && !file)}
          onClick={() => void submit()}
        >
          <Send className="size-4" />
        </Button>
      </div>
      <p className="mt-2 hidden text-center text-[11px] text-[var(--muted-foreground)] sm:block">
        Invio per spedire · Shift + Invio per andare a capo
      </p>
    </div>
  )
}
